import { addDays } from 'date-fns';
import { useState } from 'react';
import { useAccount, useConfig } from 'wagmi';
import { waitForTransactionReceipt, writeContract } from 'wagmi/actions';

import { testnetSaleFactoryContract } from 'src/global/constants/contract.constant';
import { RewardType } from 'src/global/enums/project.enum';
import { ProjectDetailType } from 'src/global/schemas/project.schema';
import { ProjectInputs } from 'src/global/types/project.type';
import { useGetNetworkTokensQuery } from 'src/store/services/network.service';
import { useUploadMetaMutation } from 'src/store/services/upload.service';
import { useAppSelector } from 'src/store/utils';
import { encodeNodeData, encodeSaleWithSelector, encodeTierPriceWithSelector } from 'src/utils/encode';
import { convertToHex, getHashFromUrl } from 'src/utils/helpers';

import { useSaveDraft } from './useSaveDraft';

const GAS_LIMIT = 10000000;

interface UseStagingPublishResponse {
  publishStagingAsync: (
    defaultProject: Partial<ProjectDetailType> & { id: string },
    project: ProjectInputs,
    draftData: any,
  ) => Promise<string>;
  isPublishing: boolean;
}

export function useStagingPublish(): UseStagingPublishResponse {
  const { address } = useAccount();
  const config = useConfig();
  const { saveDraftAsync } = useSaveDraft();
  const [uploadMeta] = useUploadMetaMutation();
  const { networkInfo } = useAppSelector(state => state.app);
  const [isPublishing, setIsPublishing] = useState(false);

  const { data: networkTokens } = useGetNetworkTokensQuery(
    { networkId: networkInfo?.id || '' },
    { skip: !networkInfo?.id },
  );

  const publishStagingAsync = async (
    defaultProject: Partial<ProjectDetailType> & { id: string },
    project: ProjectInputs,
    draftData: ProjectInputs,
  ) => {
    if (!networkInfo || !networkInfo.factory || !networkTokens) return '';
    setIsPublishing(true);
    try {
      const { id: programId } = defaultProject;
      // localStorage.removeItem('wagmi.store'); // track issue: https://github.com/wevm/wagmi/issues/3425
      const baseUriRes = await uploadMeta({
        data: {
          programId,
          name: project.node.name,
          description: project.node.description,
          image: `ipfs://${getHashFromUrl(draftData.node.asset)}`,
          edition: 1,
        },
        address: address!,
      }).unwrap();

      // initialize data
      // 1. initialize sale contract
      const payments = networkTokens.map(item => item.addr) as `0x${string}`[];
      const saleData = encodeSaleWithSelector(
        new Date(project.setting.startedAt).getTime() / 1000,
        project.phases,
        payments,
        project.referral.rewardType === RewardType.ERC_20 ? (project.referral?.percentage ?? 0) * 100 : 0,
        project.referral.rewardType === RewardType.NFT_NODE,
      );

      const tierPriceData = encodeTierPriceWithSelector(
        project.tier.layer.map(layer => layer.total),
        project.tier.layer.map(layer => layer.price * 10 ** 8),
        project.tier.autoAdjustment ? project.tier.supplyIn : 0,
        project.tier.autoAdjustment ? project.tier.pricePer * 10 ** 8 : 0,
        project.tier.autoAdjustment ? project.tier.nodeSupplyCap : 0,
        project.tier.autoAdjustment ? project.tier.priceCap * 10 ** 8 : 0,
      );

      const nodeData = encodeNodeData(
        project.node.name,
        project.node.symbol,
        project.info.nodeSaleUrl || '',
        baseUriRes.hash, // TODO: Update the field name to make it more sense - when backend update it
        addDays(new Date(project.setting.startedAt), project.setting.lockTransfer | 0).getTime() / 1000,
        project.royalties.length !== 0 ? project.royalties.reduce((total, entry) => total + entry.percent, 0) * 100 : 0,
      );

      const hash = await writeContract(config, {
        // Todo: investigate why other chain work well with default but not polygon chain
        ...(networkInfo.symbol === 'matic' ? { gas: BigInt(GAS_LIMIT) } : {}),
        ...testnetSaleFactoryContract,
        address: networkInfo.factory as `0x${string}`,
        functionName: 'createProject',
        args: [
          {
            projectId: convertToHex(`0000000${programId}`),
            saleDeployerIndex: 0,
            tierPriceDeployerIndex: 0,
            nodeDeployerIndex: 0,
            saleInitData: saleData,
            tierPriceInitData: tierPriceData,
            nodeInitData: nodeData,
          },
        ],
      });
      await waitForTransactionReceipt(config, { hash });
      return hash;
    } catch (error) {
      console.log({ error });
      throw error;
    } finally {
      setIsPublishing(false);
    }
  };

  return {
    publishStagingAsync,
    isPublishing,
  };
}
